"use client"

import type React from "react"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import "./Auth.css"

const Register: React.FC = () => {
  const [username, setUsername] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [userType, setUserType] = useState("buyer")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showVerification, setShowVerification] = useState(false)

  const { register, error: authError } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!username || !email || !password || !confirmPassword) {
      setError("Please fill in all required fields")
      return
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match")
      return
    }

    try {
      setLoading(true)
      setError("")

      // Send all required fields to the backend
      await register({
        username,
        email,
        phoneNumber,
        password,
        confirmPassword, // Add this to match backend expectations
        accessLevel: userType,
      })

      // If registration is successful, navigate to home
      navigate("/")
    } catch (err: any) {
      // Display the error from the backend if available
      setError(err.response?.data?.error || "Registration failed. Please try again.")
      console.error("Registration error:", err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-container">
      <div className="auth-card register-card">
        <div className="auth-header">
          <h1>MAACKIA'S CULT</h1>
          <p>"i will BATHE in your blood and revel in your screams"</p>
        </div>

        <div className="auth-form">
          <h2>Register New User</h2>

          {error && <div className="auth-error">{error}</div>}
          {authError && <div className="auth-error">{authError}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Username*</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">UTM Email Address*</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="eg. bob@utm.graduate.my"
                required
              />
              <small className="form-hint">Must be a valid UTM email (utm.my, utm.graduate.my, graduate.utm.my)</small>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter your phone number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password*</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              <small className="form-hint">
                Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase
                letter, one digit, and one special character.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password*</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
              />
            </div>

            <div className="form-group">
              <label>Are you a Seller or a Buyer?</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="userType"
                    value="seller"
                    checked={userType === "seller"}
                    onChange={() => setUserType("seller")}
                  />
                  Seller
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="userType"
                    value="buyer"
                    checked={userType === "buyer"}
                    onChange={() => setUserType("buyer")}
                  />
                  Buyer
                </label>
              </div>
            </div>

            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? "Processing..." : "Register"}
            </button>

            <div className="auth-links">
              <Link to="/login">Already have an account? Login</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Register
